import {
    ErrorResponse,
    User,
    UserManager,
    WebStorageStateStore
} from 'oidc-client-ts';
import {
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    useLocation,
    useNavigate
} from 'react-router-dom';

import {
    HOME,
    LOGIN_POPUP,
    LOGIN_REDIRECT
} from '@constants/routes';

import type {
    AppAuth
} from './types';

import buildAppAuthUser from './buildAppAuthUser';
import AppAuthContext from './useAppAuth';

type CognitoAppAuthProviderProps = {
    children: ReactNode;
};

type ClearStoredUserOptions = {
    reason: string;
    revokeRefreshToken?: boolean;
};

type LoginMethod = 'popup' | 'redirect';

type BuildSigninArgsOptions = {
    isAutoSSO?: boolean;
    prompt?: 'login' | 'none';
    stripSSOFromReturnTo?: boolean;
};

type SigninCallbackRequest = {
    requestKey: string;
    result: ReturnType<UserManager['signinCallback']>;
};

type ValidCognitoOIDCUser = User & {
    readonly expired: false | undefined;
};

const AUTH_LOG_PREFIX = '[CognitoAppAuthProvider]';

const FORCE_PROMPT_LOGIN_ONCE_KEY = 'forcePromptLoginOnce';
const PENDING_AUTO_SSO_RETURN_TO_KEY = 'pendingAutoSsoReturnTo';
const SSO_SEARCH_PARAM = 'sso';
const SSO_SEARCH_PARAM_VALUE = '1';

const getOIDCUserStorageKey = (): string => `user:${process.env.COGNITO_AUTHORITY}:${process.env.COGNITO_CLIENT_ID}`;

const getAppDomainUrl = (): string => window.location.origin;

const getCognitoRedirectUrl = (): string => new URL(LOGIN_REDIRECT, getAppDomainUrl()).toString();

const getCognitoCallbackPath = (): string => new URL(getCognitoRedirectUrl()).pathname;

const getCognitoPopupRedirectUrl = (): string => new URL(LOGIN_POPUP, getAppDomainUrl()).toString();

const getCognitoPopupCallbackPath = (): string => new URL(getCognitoPopupRedirectUrl()).pathname;

const getCognitoLogoutRedirectUrl = (): string => new URL('/', getAppDomainUrl()).toString();

const isSafeInternalReturnTo = (value: string): boolean => value.startsWith('/') && !value.startsWith('//');

const stripSearchParam = (search: string, paramName: string): string => {
    const params = new URLSearchParams(search);

    params.delete(paramName);

    const nextSearch = params.toString();

    return nextSearch ? `?${nextSearch}` : '';
};

const isAutoSSORequested = (search: string): boolean => {
    const params = new URLSearchParams(search);

    return params.get(SSO_SEARCH_PARAM) === SSO_SEARCH_PARAM_VALUE;
};

const getReturnToPath = (
    pathname: string,
    search: string,
    hash: string,
    {
        stripSso = false
    }: {
        stripSso?: boolean;
    } = {}
): string => {
    const nextSearch = stripSso ? stripSearchParam(search, SSO_SEARCH_PARAM) : search;

    return `${pathname}${nextSearch}${hash}`;
};

const stripSSOFromReturnTo = (returnTo: string): string => {
    if (!isSafeInternalReturnTo(returnTo)) {
        return HOME;
    }

    try {
        const url = new URL(returnTo, getAppDomainUrl());

        url.searchParams.delete(SSO_SEARCH_PARAM);

        return `${url.pathname}${url.search}${url.hash}` || HOME;
    } catch {
        return HOME;
    }
};

const getReturnToFromState = (state: unknown): string | undefined => {
    if (!state || typeof state !== 'object') {
        return undefined;
    }

    const {
        returnTo
    } = state as {
        returnTo?: unknown;
    };

    return (typeof returnTo === 'string') ? returnTo : undefined;
};

const getSafeReturnTo = (state: unknown): string => {
    const returnTo = getReturnToFromState(state);

    if (!returnTo || !isSafeInternalReturnTo(returnTo)) {
        return HOME;
    }

    return stripSSOFromReturnTo(returnTo);
};

const getPendingAutoSsoReturnTo = (): string | null => sessionStorage.getItem(PENDING_AUTO_SSO_RETURN_TO_KEY);

const clearPendingAutoSsoReturnTo = (): void => {
    sessionStorage.removeItem(PENDING_AUTO_SSO_RETURN_TO_KEY);
};

const consumePendingAutoSsoReturnTo = (): string => {
    const returnTo = getPendingAutoSsoReturnTo();

    clearPendingAutoSsoReturnTo();

    return returnTo ? stripSSOFromReturnTo(returnTo) : HOME;
};

const buildCognitoLogoutUrl = (): string => {
    const url = new URL('/logout', process.env.COGNITO_DOMAIN);

    url.searchParams.set('client_id', process.env.COGNITO_CLIENT_ID!);
    url.searchParams.set('logout_uri', getCognitoLogoutRedirectUrl());

    return url.toString();
};

const logAuthError = (message: string, error: unknown) => {
    console.error(`${AUTH_LOG_PREFIX} ${message}`);
    console.error(error);
};

const isValidCognitoOIDCUser = (user?: User | null): user is ValidCognitoOIDCUser => Boolean(
    user
    && !user.expired
    && buildAppAuthUser(user.id_token)
);

const CognitoAppAuthProvider = (props: CognitoAppAuthProviderProps) => {
    const {
        children
    } = props;

    const location = useLocation();
    const navigate = useNavigate();

    // Shares callback processing across React Strict Mode effect replays.
    const signinCallbackRequestRef = useRef<SigninCallbackRequest | null>(null);

    // Tracks an automatic SSO redirect so React Strict Mode does not start it twice.
    const startedAutoSsoRequestRef = useRef<string | null>(null);

    // Dedupes concurrent manual token refresh attempts.
    const refreshPromiseRef = useRef<Promise<User | null> | null>(null);

    const [
        userManager
    ] = useState<UserManager>(() => new UserManager({
        authority: process.env.COGNITO_AUTHORITY!,
        automaticSilentRenew: false,
        client_id: process.env.COGNITO_CLIENT_ID!,
        popupWindowFeatures: {
            height: 635,
            location: false,
            resizable: true,
            scrollbars: true,
            toolbar: false,
            width: 380
        },
        popup_redirect_uri: getCognitoPopupRedirectUrl(),
        post_logout_redirect_uri: getCognitoLogoutRedirectUrl(),
        redirect_uri: getCognitoRedirectUrl(),
        response_type: 'code',
        revokeTokenTypes: [
            'refresh_token'
        ],
        scope: process.env.COGNITO_SCOPE,
        userStore: new WebStorageStateStore({
            store: window.localStorage
        })
    }));

    const [
        isLoading,
        setIsLoading
    ] = useState<boolean>(true);

    const [
        oidcUser,
        setOidcUser
    ] = useState<User | null>(null);

    const appAuthUser = useMemo(
        () => (isValidCognitoOIDCUser(oidcUser) ? buildAppAuthUser(oidcUser.id_token) : undefined),
        [
            oidcUser
        ]
    );

    const hasValidCognitoUser = !!appAuthUser;

    const clearStoredUser = useCallback(async ({
        reason,
        revokeRefreshToken = false
    }: ClearStoredUserOptions) => {
        console.info(`${AUTH_LOG_PREFIX} ${reason}`);

        try {
            const currentUser = await userManager.getUser();

            // Revoke the refresh token before clearing local state when requested.
            if (revokeRefreshToken && currentUser?.refresh_token) {
                try {
                    await userManager.revokeTokens([
                        'refresh_token'
                    ]);
                } catch (error) {
                    logAuthError('Failed to revoke refresh token...', error);
                }
            }
        } catch (error) {
            logAuthError('Failed to read current OIDC user during cleanup...', error);
        }

        try {
            await userManager.removeUser();
        } catch (error) {
            logAuthError('Failed to remove OIDC user from storage...', error);
        }

        setOidcUser(null);

        try {
            await userManager.clearStaleState();
        } catch (error) {
            logAuthError('Failed to clear stale OIDC state...', error);
        }
    }, [
        userManager
    ]);

    const refreshUser = useCallback(async (): Promise<User | null> => {
        if (!refreshPromiseRef.current) {
            refreshPromiseRef.current = userManager.signinSilent()
                .then(async (refreshedUser) => {
                    if (isValidCognitoOIDCUser(refreshedUser)) {
                        setOidcUser(refreshedUser);

                        return refreshedUser;
                    }

                    await clearStoredUser({
                        reason: 'Silent refresh returned an expired or invalid Cognito user...'
                    });

                    return null;
                })
                .finally(() => {
                    refreshPromiseRef.current = null;
                });
        }

        return refreshPromiseRef.current;
    }, [
        clearStoredUser,
        userManager
    ]);

    const processSigninCallback = useCallback((requestKey: string): ReturnType<UserManager['signinCallback']> => {
        if (signinCallbackRequestRef.current?.requestKey === requestKey) {
            return signinCallbackRequestRef.current.result;
        }

        const result = userManager.signinCallback();

        signinCallbackRequestRef.current = {
            requestKey,
            result
        };

        return result;
    }, [
        userManager
    ]);

    useEffect(() => {
        // Removes abandoned authorization state entries from previous incomplete flows.
        userManager.clearStaleState().catch((error) => {
            logAuthError('Failed to clear stale OIDC state during initialization...', error);
        });
    }, [
        userManager
    ]);

    useEffect(() => {
        const handleUserLoaded = (loadedUser: User) => {
            setOidcUser(loadedUser);
        };

        const handleUserUnloaded = () => {
            setOidcUser(null);
        };

        const handleAccessTokenExpiring = () => {
            refreshUser().catch((error) => {
                logAuthError('Failed to refresh access token before expiration...', error);

                clearStoredUser({
                    reason: 'Access token refresh failed before expiration. Clearing local session...'
                });
            });
        };

        const handleAccessTokenExpired = () => {
            clearStoredUser({
                reason: 'Access token expired. Clearing local session...'
            });
        };

        const handleSilentRenewError = (error: Error) => {
            logAuthError('Silent renew error received...', error);

            clearStoredUser({
                reason: 'Silent renew failed. Clearing local session...'
            });
        };

        const handleUserSignedOut = () => {
            clearStoredUser({
                reason: 'User signed out from the Cognito session...'
            });
        };

        userManager.events.addUserLoaded(handleUserLoaded);
        userManager.events.addUserUnloaded(handleUserUnloaded);
        userManager.events.addAccessTokenExpiring(handleAccessTokenExpiring);
        userManager.events.addAccessTokenExpired(handleAccessTokenExpired);
        userManager.events.addSilentRenewError(handleSilentRenewError);
        userManager.events.addUserSignedOut(handleUserSignedOut);

        return () => {
            userManager.events.removeUserLoaded(handleUserLoaded);
            userManager.events.removeUserUnloaded(handleUserUnloaded);
            userManager.events.removeAccessTokenExpiring(handleAccessTokenExpiring);
            userManager.events.removeAccessTokenExpired(handleAccessTokenExpired);
            userManager.events.removeSilentRenewError(handleSilentRenewError);
            userManager.events.removeUserSignedOut(handleUserSignedOut);
        };
    }, [
        clearStoredUser,
        refreshUser,
        userManager
    ]);

    useEffect(() => {
        let isSubscribed = true;

        const handleUserStorageChange = (event: StorageEvent) => {
            if (event.storageArea !== window.localStorage || event.key !== getOIDCUserStorageKey()) {
                return;
            }

            if (!event.newValue) {
                setOidcUser(null);

                return;
            }

            userManager.getUser()
                .then((storedUser) => {
                    if (isSubscribed) {
                        setOidcUser(isValidCognitoOIDCUser(storedUser) ? storedUser : null);
                    }
                })
                .catch((error) => {
                    logAuthError('Failed to synchronize Cognito user from another tab...', error);
                });
        };

        window.addEventListener('storage', handleUserStorageChange);

        return () => {
            isSubscribed = false;
            window.removeEventListener('storage', handleUserStorageChange);
        };
    }, [
        userManager
    ]);

    const buildSigninArgs = useCallback(({
        isAutoSSO = false,
        prompt,
        stripSSOFromReturnTo: shouldstripSSOFromReturnTo = false
    }: BuildSigninArgsOptions = {}) => {
        const redirectCallbackPath = getCognitoCallbackPath();
        const popupCallbackPath = getCognitoPopupCallbackPath();
        const returnTo = getReturnToPath(location.pathname, location.search, location.hash, {
            stripSso: shouldstripSSOFromReturnTo
        });

        const safeReturnTo = (returnTo === redirectCallbackPath || returnTo === popupCallbackPath) ? HOME : returnTo;

        const extraQueryParams: Record<string, string> = {};

        if (prompt) {
            extraQueryParams.prompt = prompt;
        } else if (!isAutoSSO) {
            // Forces Cognito to show the login prompt once after an app-initiated logout.
            const shouldForcePromptLogin = sessionStorage.getItem(FORCE_PROMPT_LOGIN_ONCE_KEY) === 'true';

            if (shouldForcePromptLogin) {
                sessionStorage.removeItem(FORCE_PROMPT_LOGIN_ONCE_KEY);
                extraQueryParams.prompt = 'login';
            }
        }

        return ({
            extraQueryParams: Object.keys(extraQueryParams).length ? extraQueryParams : undefined,
            state: {
                autoSso: isAutoSSO,
                returnTo: safeReturnTo
            }
        });
    }, [
        location.hash,
        location.pathname,
        location.search
    ]);

    useEffect(() => {
        let isSubscribed = true;

        const hydrateUser = async () => {
            const redirectCallbackPath = getCognitoCallbackPath();
            const popupCallbackPath = getCognitoPopupCallbackPath();

            const isRedirectCallbackPath = location.pathname === redirectCallbackPath;
            const isPopupCallbackPath = location.pathname === popupCallbackPath;
            const isAnyCallbackPath = isRedirectCallbackPath || isPopupCallbackPath;

            const params = new URLSearchParams(location.search);
            const code = params.get('code');
            const authError = params.get('error');
            const authErrorDescription = params.get('error_description');

            const ssoRequested = isAutoSSORequested(location.search);

            const returnToWithoutSso = getReturnToPath(location.pathname, location.search, location.hash, {
                stripSso: true
            });

            // Cognito sends either an auth code or an error back to the callback URL.
            const isCognitoCallback = isAnyCallbackPath && (Boolean(code) || Boolean(authError));
            const callbackRequestKey = `${location.pathname}${location.search}`;

            // Reset callback and automatic SSO dedupe markers after leaving callback / SSO routes.
            if (!isAnyCallbackPath) {
                signinCallbackRequestRef.current = null;
            }

            if (!ssoRequested) {
                startedAutoSsoRequestRef.current = null;
            }

            try {
                setIsLoading(true);

                if (isCognitoCallback) {
                    if (isPopupCallbackPath) {
                        // Popup callbacks must be handled by oidc-client-ts so the opener receives success or error.
                        await processSigninCallback(callbackRequestKey);

                        return;
                    }

                    if (authError) {
                        try {
                            // This validates the callback state and removes the completed request
                            // entry before we decide whether the provider error is recoverable.
                            await processSigninCallback(callbackRequestKey);

                            if (!isSubscribed) {
                                return;
                            }
                        } catch (error) {
                            if (!isSubscribed) {
                                return;
                            }

                            const pendingAutoSsoReturnTo = getPendingAutoSsoReturnTo();

                            // This is the expected "not logged into Cognito anymore" result
                            // for an opportunistic SSO attempt with prompt=none.
                            if (
                                pendingAutoSsoReturnTo
                                && error instanceof ErrorResponse
                                && error.error === 'login_required'
                            ) {
                                const safeReturnTo = consumePendingAutoSsoReturnTo();

                                await clearStoredUser({
                                    reason: 'Automatic SSO did not find an active Cognito session. Continuing logged out...'
                                });

                                if (!isSubscribed) {
                                    return;
                                }

                                navigate(safeReturnTo, {
                                    replace: true
                                });

                                return;
                            }

                            throw error;
                        }

                        clearPendingAutoSsoReturnTo();

                        const message = authErrorDescription
                            ? `Cognito callback error: ${authError} - ${authErrorDescription}`
                            : `Cognito callback error: ${authError}`;

                        throw new Error(message);
                    }

                    const signedInUser = await processSigninCallback(callbackRequestKey);

                    if (!isSubscribed) {
                        return;
                    }

                    clearPendingAutoSsoReturnTo();

                    if (!isValidCognitoOIDCUser(signedInUser)) {
                        await clearStoredUser({
                            reason: 'Received an expired or invalid user from the Cognito callback...'
                        });

                        if (!isSubscribed) {
                            return;
                        }

                        navigate(HOME, {
                            replace: true
                        });

                        return;
                    }

                    setOidcUser(signedInUser);

                    // Restore the pre-login route when it is a safe in-app path.
                    navigate(getSafeReturnTo(signedInUser.state), {
                        replace: true
                    });

                    return;
                }

                // Hydrate auth state from storage on normal app loads.
                const retrievedUser = await userManager.getUser();

                if (!isSubscribed) {
                    return;
                }

                if (isValidCognitoOIDCUser(retrievedUser)) {
                    setOidcUser(retrievedUser);

                    // If a signed-in user lands on /somewhere?sso=1, remove the one-time SSO marker
                    // so refreshes/bookmarks don't keep carrying it around.
                    if (ssoRequested && returnToWithoutSso !== getReturnToPath(location.pathname, location.search, location.hash)) {
                        navigate(returnToWithoutSso, {
                            replace: true
                        });
                    }

                    return;
                }

                // A new tab can open after the shared access token expires while its refresh token
                // is still valid, so renew that session before treating it as logged out.
                if (retrievedUser?.expired && retrievedUser.refresh_token) {
                    const refreshedUser = await refreshUser();

                    if (isValidCognitoOIDCUser(refreshedUser)) {
                        return;
                    }
                }

                // Expired or invalid stored users are cleared so the app state matches token validity.
                if (retrievedUser) {
                    await clearStoredUser({
                        reason: 'Stored OIDC user is expired or invalid during hydration...'
                    });

                    if (!isSubscribed) {
                        return;
                    }
                }

                if (ssoRequested) {
                    const autoSsoRequestKey = `${location.pathname}${location.search}${location.hash}`;

                    // Avoid duplicate redirects in React Strict Mode or rapid location effect reruns.
                    if (startedAutoSsoRequestRef.current === autoSsoRequestKey) {
                        return;
                    }

                    startedAutoSsoRequestRef.current = autoSsoRequestKey;

                    sessionStorage.setItem(PENDING_AUTO_SSO_RETURN_TO_KEY, returnToWithoutSso);

                    await userManager.signinRedirect(buildSigninArgs({
                        isAutoSSO: true,
                        prompt: 'none',
                        stripSSOFromReturnTo: true
                    }));

                    return;
                }

                setOidcUser(null);
            } catch (error) {
                logAuthError('Failed to hydrate Cognito user...', error);

                if (!isSubscribed) {
                    return;
                }

                clearPendingAutoSsoReturnTo();

                await clearStoredUser({
                    reason: 'Failed to process Cognito authentication state...'
                });

                if (!isSubscribed) {
                    return;
                }

                // Leave redirect callback routes after an auth failure so the app can recover cleanly.
                if (isRedirectCallbackPath) {
                    navigate(HOME, {
                        replace: true
                    });
                }
            } finally {
                if (isSubscribed) {
                    setIsLoading(false);
                }
            }
        };

        hydrateUser();

        return () => {
            isSubscribed = false;
        };
    }, [
        buildSigninArgs,
        clearStoredUser,
        location.hash,
        location.pathname,
        location.search,
        navigate,
        processSigninCallback,
        refreshUser,
        userManager
    ]);

    const startLogin = useCallback(async (method: LoginMethod) => {
        setIsLoading(true);

        try {
            const signinArgs = buildSigninArgs();

            if (method === 'popup') {
                const signedInUser = await userManager.signinPopup(signinArgs);

                if (!isValidCognitoOIDCUser(signedInUser)) {
                    await clearStoredUser({
                        reason: 'Popup login returned an expired or invalid Cognito user...'
                    });

                    throw new Error('Popup login did not return a valid Cognito user.');
                }

                setOidcUser(signedInUser);

                return;
            }

            await userManager.signinRedirect(signinArgs);
        } catch (error) {
            setIsLoading(false);
            throw error;
        } finally {
            if (method === 'popup') {
                setIsLoading(false);
            }
        }
    }, [
        buildSigninArgs,
        clearStoredUser,
        userManager
    ]);

    const loginWithPopup = useCallback(async () => {
        await startLogin('popup');
    }, [
        startLogin
    ]);

    const loginWithRedirect = useCallback(async () => {
        await startLogin('redirect');
    }, [
        startLogin
    ]);

    const appAuth = useMemo<AppAuth>(() => ({
        getAccessToken: async () => {
            // Always read the latest user snapshot from storage before returning a token.
            let retrievedUser = await userManager.getUser();

            if (isValidCognitoOIDCUser(retrievedUser)) {
                return retrievedUser.access_token;
            }

            try {
                retrievedUser = await refreshUser();

                if (isValidCognitoOIDCUser(retrievedUser)) {
                    return retrievedUser.access_token;
                }
            } catch (error) {
                logAuthError('Failed to refresh access token on demand...', error);
            }

            await clearStoredUser({
                reason: 'Access token requested but no valid OIDC user is available...'
            });

            return undefined;
        },

        hasPermission: (requiredPermission?: string) => !requiredPermission || (appAuthUser?.rolesList?.includes(requiredPermission) ?? false),

        isAuthenticated: hasValidCognitoUser,

        isLoading,

        login: loginWithRedirect,

        loginWithPopup,

        logout: async () => {
            // Ensures the next interactive login starts from a fresh Cognito prompt.
            sessionStorage.setItem(FORCE_PROMPT_LOGIN_ONCE_KEY, 'true');
            setIsLoading(true);

            await clearStoredUser({
                reason: 'User initiated logout...',
                revokeRefreshToken: true
            });

            // Finish logout at the Cognito hosted UI so the provider session is cleared too.
            window.location.replace(buildCognitoLogoutUrl());
        },

        user: appAuthUser
    }), [
        appAuthUser,
        clearStoredUser,
        hasValidCognitoUser,
        isLoading,
        loginWithPopup,
        loginWithRedirect,
        refreshUser,
        userManager
    ]);

    return (
        <AppAuthContext.Provider value={appAuth}>
            {children}
        </AppAuthContext.Provider>
    );
};

export default CognitoAppAuthProvider;
